import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { restrictedDevicesAPI, campaignsAPI } from '../services/api';
import { RestrictedDevice, Campaign } from '../types';
import { generateDeviceId } from '../utils/deviceId';
import { getAgentMachineInfo } from '../utils/deviceAssertion';

const DeviceRestrictionsPage: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<RestrictedDevice | null>(null);
  const [deviceId, setDeviceId] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [sharedWorkstation, setSharedWorkstation] = useState(false);
  const [machineDeviceId, setMachineDeviceId] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  // Browser fingerprint ID for this browser (fallback identity when no Agent is present).
  const [browserDeviceId] = useState<string>(() => generateDeviceId());
  // Physical machine ID reported by the local Windows Agent, when one is running on this computer.
  const [agentMachineId, setAgentMachineId] = useState<string | null>(null);
  const [agentVersion, setAgentVersion] = useState<string | null>(null);
  const [agentAssertionVerified, setAgentAssertionVerified] = useState(false);
  const [agentChecked, setAgentChecked] = useState(false);

  const queryClient = useQueryClient();

  // When the TEKS TIME Agent is running on this computer, read its machine device ID from the local
  // bridge so we can register the physical machine (not just the spoofable browser fingerprint).
  useEffect(() => {
    let active = true;
    getAgentMachineInfo()
      .then((info) => {
        if (active) {
          setAgentMachineId(info?.deviceId ?? null);
          setAgentVersion(info?.version ?? null);
          setAgentAssertionVerified(Boolean(info?.assertionVerified));
          setAgentChecked(true);
        }
      })
      .catch(() => {
        if (active) {
          setAgentChecked(true);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  // Fetch restricted devices
  const { data: devicesData, isLoading } = useQuery({
    queryKey: ['restricted-devices'],
    queryFn: () => restrictedDevicesAPI.getAll(),
    select: (response) => response.data.data,
  });

  // Fetch all campaigns for the multi-select
  const { data: campaignsData } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignsAPI.getAll(),
    select: (response) => response.data.data || [],
  });

  // Create device mutation
  const createMutation = useMutation({
    mutationFn: (device: {
      deviceId: string;
      deviceName?: string;
      allowedCampaignIds: string[];
      allowedRoleIds: string[];
      sharedWorkstation?: boolean;
      machineDeviceId?: string;
    }) =>
      restrictedDevicesAPI.create(device),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restricted-devices'] });
      setMessage({ type: 'success', text: 'Device added to restricted list successfully' });
      handleCloseDialog();
    },
    onError: (error: any) => {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to add device',
      });
    },
  });

  // Update device mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, device }: {
      id: number;
      device: {
        deviceName?: string;
        allowedCampaignIds?: string[];
        allowedRoleIds?: string[];
        isActive?: boolean;
        sharedWorkstation?: boolean;
        machineDeviceId?: string;
      };
    }) =>
      restrictedDevicesAPI.update(id, device),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restricted-devices'] });
      setMessage({ type: 'success', text: 'Device updated successfully' });
      handleCloseDialog();
    },
    onError: (error: any) => {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update device',
      });
    },
  });

  // Delete device mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => restrictedDevicesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restricted-devices'] });
      setMessage({ type: 'success', text: 'Device removed from restricted list' });
    },
    onError: (error: any) => {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to remove device',
      });
    },
  });

  const handleOpenDialog = (device?: RestrictedDevice) => {
    if (device) {
      setEditingDevice(device);
      setDeviceId(device.deviceId);
      setDeviceName(device.deviceName || '');
      setSelectedCampaignIds(device.allowedCampaignIds || []);
      setSelectedRoleIds(device.allowedRoleIds || []);
      setSharedWorkstation(Boolean(device.sharedWorkstation));
      setMachineDeviceId((device as any).machineDeviceId || '');
    } else {
      setEditingDevice(null);
      setDeviceId(browserDeviceId);
      setDeviceName('');
      setSelectedCampaignIds([]);
      setSelectedRoleIds([]);
      setSharedWorkstation(false);
      // When the Agent is present, bind the restriction to the physical machine automatically.
      setMachineDeviceId(agentMachineId || '');
    }
    setDialogOpen(true);
    setMessage(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDevice(null);
    setDeviceId('');
    setDeviceName('');
    setSelectedCampaignIds([]);
    setSelectedRoleIds([]);
    setSharedWorkstation(false);
    setMachineDeviceId('');
    setMessage(null);
  };

  const handleSubmit = () => {
    if (!deviceId.trim()) {
      setMessage({ type: 'error', text: 'Device ID is required' });
      return;
    }

    if (selectedCampaignIds.length === 0) {
      setMessage({ type: 'error', text: 'At least one campaign must be selected' });
      return;
    }

    if (selectedRoleIds.length === 0) {
      setMessage({ type: 'error', text: 'At least one role must be selected' });
      return;
    }

    const trimmedDeviceName = deviceName.trim();
    
    if (editingDevice) {
      updateMutation.mutate({
        id: editingDevice.id,
        device: {
          deviceName: trimmedDeviceName || undefined,
          allowedCampaignIds: selectedCampaignIds,
          allowedRoleIds: selectedRoleIds,
          sharedWorkstation,
          machineDeviceId: machineDeviceId.trim() || undefined,
        },
      });
    } else {
      createMutation.mutate({
        deviceId: deviceId.trim(),
        deviceName: trimmedDeviceName || undefined,
        allowedCampaignIds: selectedCampaignIds,
        allowedRoleIds: selectedRoleIds,
        sharedWorkstation,
        machineDeviceId: machineDeviceId.trim() || undefined,
      });
    }
  };

  const handleToggleActive = (device: RestrictedDevice) => {
    updateMutation.mutate({
      id: device.id,
      device: {
        isActive: !device.isActive,
      },
    });
  };

  const handleToggleSharedWorkstation = (device: RestrictedDevice) => {
    updateMutation.mutate({
      id: device.id,
      device: {
        sharedWorkstation: !device.sharedWorkstation,
      },
    });
  };

  const handleDelete = (device: RestrictedDevice) => {
    if (window.confirm(`Are you sure you want to remove "${device.deviceName || device.deviceId}" from the restricted list?`)) {
      deleteMutation.mutate(device.id);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Device Restrictions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Manage computers that are restricted to specific campaigns. Enable Shared Workstation on lobby/training PCs so TEKS TIME never remembers the previous employee after logout.
        </Typography>
        <Alert severity={agentMachineId ? 'success' : 'info'} sx={{ mb: 2 }}>
          {agentMachineId ? (
            <>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                TEKS TIME Agent detected{agentVersion ? ` (v${agentVersion})` : ''}:
              </Typography>
              <Typography variant="body2" fontFamily="monospace" sx={{ bgcolor: 'rgba(0,0,0,0.05)', p: 1, borderRadius: 1 }}>
                {agentMachineId}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {agentAssertionVerified
                  ? 'Machine ID verified by the Agent signed assertion. Registering with this ID binds the restriction to the computer (not the browser).'
                  : 'Machine ID reported by the local Agent health bridge. Signed assertion is not ready yet — reinstall/restart the Agent if machine-level login enforcement is required.'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Browser ID (fallback): <Box component="code" sx={{ fontFamily: 'monospace' }}>{browserDeviceId}</Box>
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Your Current Device ID:
              </Typography>
              <Typography variant="body2" fontFamily="monospace" sx={{ bgcolor: 'rgba(0,0,0,0.05)', p: 1, borderRadius: 1 }}>
                {browserDeviceId}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {agentChecked
                  ? 'No TEKS TIME Agent detected on this computer — this restriction applies at the browser level. Open this page in a browser on the Agent PC (localhost bridge). Install the Agent for machine-level (per-computer) enforcement.'
                  : 'Checking for TEKS TIME Agent on this computer…'}
              </Typography>
            </>
          )}
        </Alert>
      </Box>

      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      <Card>
        <CardHeader
          title="Training Computer Restrictions"
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add New Computer
            </Button>
          }
        />
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Device ID</TableCell>
                  <TableCell>Device Name</TableCell>
                  <TableCell>Machine</TableCell>
                  <TableCell>Allowed Campaigns</TableCell>
                  <TableCell>Allowed Roles</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Shared Workstation</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : !devicesData || devicesData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No restricted devices. All computers allow all campaigns and roles by default.
                    </TableCell>
                  </TableRow>
                ) : (
                  devicesData.map((device) => (
                    <TableRow key={device.id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {device.deviceId}
                        </Typography>
                      </TableCell>
                      <TableCell>{device.deviceName || '-'}</TableCell>
                      <TableCell>
                        {(device as any).machineDeviceId ? (
                          <Tooltip title="Bound to a physical machine. On a Shared device, login/clock-in is verified by the Agent's signed assertion (fail-closed) instead of the browser ID.">
                            <Chip
                              icon={<ComputerIcon />}
                              label={(device as any).machineDeviceId}
                              color="success"
                              size="small"
                              variant="outlined"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="No machine binding. Restriction keyed to the browser fingerprint only (spoofable). Register with the Agent present to bind the physical computer.">
                            <Chip label="Browser only" size="small" variant="outlined" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {device.allowedCampaignIds && device.allowedCampaignIds.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {device.allowedCampaignIds.map((campaignId) => {
                              const campaign = campaignsData?.find((c: Campaign) => c.id === campaignId);
                              return (
                                <Chip
                                  key={campaignId}
                                  label={campaign?.name || campaignId.substring(0, 8)}
                                  color="primary"
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No campaigns selected
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {device.allowedRoleIds && device.allowedRoleIds.length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {device.allowedRoleIds.map((roleId) => {
                              const roleDisplayNames: Record<string, string> = {
                                'super_user': 'Super Usuario',
                                'administrator': 'Administrador',
                                'manager': 'Gerente',
                                'supervisor': 'Supervisor',
                                'team_lead': 'Líder de Equipo',
                                'agent': 'Agente',
                                'other': 'Otro'
                              };
                              return (
                                <Chip
                                  key={roleId}
                                  label={roleDisplayNames[roleId] || roleId}
                                  color="secondary"
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No roles selected
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={device.isActive}
                              onChange={() => handleToggleActive(device)}
                              size="small"
                            />
                          }
                          label={device.isActive ? 'Active' : 'Inactive'}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="When enabled, web login never remembers the previous employee email after logout.">
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(device.sharedWorkstation)}
                                onChange={() => handleToggleSharedWorkstation(device)}
                                size="small"
                                inputProps={{ 'aria-label': 'Shared workstation' }}
                              />
                            }
                            label={device.sharedWorkstation ? 'Shared' : 'Dedicated'}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(device)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(device)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDevice ? 'Edit Restricted Device' : 'Add New Restricted Computer'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Super Users are exempt from campaign and role restrictions on registered computers.
            </Alert>
            <TextField
              fullWidth
              label="Device ID"
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              margin="normal"
              required
              disabled={!!editingDevice}
              helperText={editingDevice ? 'Device ID cannot be changed' : 'Auto-detected from browser fingerprint'}
            />
            <TextField
              fullWidth
              label="Device Name"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              margin="normal"
              placeholder="e.g., Room 1 Machine"
              helperText="Optional friendly name for this device"
            />
            <TextField
              fullWidth
              label="Registered machine (Agent device ID) — optional"
              value={machineDeviceId}
              onChange={(e) => setMachineDeviceId(e.target.value)}
              margin="normal"
              placeholder="e.g., machine GUID reported by the TEKS TIME Agent"
              helperText="Bind this restriction to a physical computer. When set and the computer is a Shared device, login/clock-in is verified by the Agent's signed device assertion (fail-closed) instead of the spoofable browser ID above."
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Allowed Campaigns</InputLabel>
              <Select
                multiple
                value={selectedCampaignIds}
                onChange={(e) => setSelectedCampaignIds(e.target.value as string[])}
                input={<OutlinedInput label="Allowed Campaigns" />}
                renderValue={(selected) => {
                  const selectedCampaigns = (selected as string[])
                    .map(id => campaignsData?.find((c: Campaign) => c.id === id))
                    .filter((c): c is Campaign => c !== undefined)
                    .map((c) => c.name);
                  return selectedCampaigns.length > 0 
                    ? selectedCampaigns.join(', ')
                    : 'Select campaigns...';
                }}
              >
                {campaignsData && campaignsData.length > 0 ? (
                  campaignsData.map((campaign: Campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No campaigns available</MenuItem>
                )}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Select one or more campaigns. Only users belonging to these campaigns can log in on this device.
              </Typography>
            </FormControl>
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Allowed Roles</InputLabel>
              <Select
                multiple
                value={selectedRoleIds}
                onChange={(e) => setSelectedRoleIds(e.target.value as string[])}
                input={<OutlinedInput label="Allowed Roles" />}
                renderValue={(selected) => {
                  const roleDisplayNames: Record<string, string> = {
                    'super_user': 'Super Usuario',
                    'administrator': 'Administrador',
                    'manager': 'Gerente',
                    'supervisor': 'Supervisor',
                    'team_lead': 'Líder de Equipo',
                    'agent': 'Agente',
                    'other': 'Otro'
                  };
                  const selectedRoles = (selected as string[])
                    .map(role => roleDisplayNames[role] || role);
                  return selectedRoles.length > 0 
                    ? selectedRoles.join(', ')
                    : 'Select roles...';
                }}
              >
                <MenuItem value="super_user">Super Usuario</MenuItem>
                <MenuItem value="administrator">Administrador</MenuItem>
                <MenuItem value="manager">Gerente</MenuItem>
                <MenuItem value="supervisor">Supervisor</MenuItem>
                <MenuItem value="team_lead">Líder de Equipo</MenuItem>
                <MenuItem value="agent">Agente</MenuItem>
                <MenuItem value="other">Otro</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Select one or more roles. Only users with these roles can log in on this device.
              </Typography>
            </FormControl>
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch
                  checked={sharedWorkstation}
                  onChange={(e) => setSharedWorkstation(e.target.checked)}
                  inputProps={{ 'aria-label': 'Shared workstation mode' }}
                />
              }
              label="Shared workstation (never remember previous employee email after logout)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {editingDevice ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeviceRestrictionsPage;

